import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AccessMode, ActorRef } from '@/features/users/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * A equipe da Westhetics: quem é root (docs/api/admin.md, "Equipe da Westhetics"; RN-AUT-28 a 30).
 * O `id` é a pessoa: a equipe não tem vínculo com clínica. Os nomes de campo são os de
 * `apps/api/supabase/functions/admin/team/schemas.ts`.
 */

export interface TeamMember {
  id: string
  name: string
  email: string
  /**
   * O e-mail é da empresa (RN-AUT-31). Falso só enquanto ninguém da equipe tem e-mail da empresa:
   * quando a primeira pessoa com ele entrar, quem está fora sai da equipe sozinho.
   */
  hasAllowedEmail: boolean
  /** `active`: já entrou alguma vez; `invited`: ainda não. */
  status: 'active' | 'invited'
  hasTemporaryPassword: boolean
  /** Validade do convite ainda não aceito; `null` sem convite pendente. */
  inviteExpiresAt: string | null
  lastSignInAt: string | null
  /** Clínicas em que a pessoa também tem vínculo. */
  clinicCount: number
  createdAt: string
  createdBy: ActorRef
}

export interface AddTeamMemberInput {
  name: string
  email: string
  accessMode: AccessMode
}

/**
 * Como a pessoa entra. A senha temporária vem uma única vez: na repetição do mesmo pedido a API
 * devolve a resposta guardada, com `temporaryPassword` nulo. Quem já tem conta com senha só ganha o
 * acesso (`existing_account`); quem tem conta sem senha recebe o convite.
 */
export type TeamAccess =
  | { mode: 'invite'; inviteExpiresAt: string; emailSent: boolean }
  | { mode: 'temporary_password'; temporaryPassword: string | null; emailSent: boolean }
  | { mode: 'existing_account'; emailSent: boolean }

export interface AddedTeamMember {
  member: TeamMember
  access: TeamAccess
}

export interface ResentTeamInvite {
  inviteExpiresAt: string
  emailSent: boolean
}

export const teamKeys = {
  all: ['team'] as const,
  list: (params: object) => ['team', 'list', params] as const,
  detail: (id: string) => ['team', 'detail', id] as const,
}

export function useTeam(params: { search?: string; page?: number }) {
  const api = useApi()
  return useQuery({
    queryKey: teamKeys.list(params),
    queryFn: () =>
      api.list<TeamMember>('/admin/team', {
        search: params.search,
        page: params.page ?? 1,
        perPage: 25,
      }),
    placeholderData: (previous) => previous,
  })
}

export function useTeamMember(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: teamKeys.detail(id ?? ''),
    queryFn: () => api.get<TeamMember>(`/admin/team/${id}`),
    enabled: Boolean(id),
  })
}

export function useAddTeamMember() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: AddTeamMemberInput) =>
      api.post<AddedTeamMember>('/admin/team', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: (added) => {
      idempotency.reset()
      queryClient.setQueryData(teamKeys.detail(added.member.id), added.member)
      return queryClient.invalidateQueries({ queryKey: ['team', 'list'] })
    },
  })
}

/** Reemite o link de criar a senha por mais 7 dias; o anterior deixa de valer. */
export function useResendTeamInvite(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<ResentTeamInvite>(`/admin/team/${id}/resend-invite`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: teamKeys.all }),
  })
}

/**
 * Tirar da equipe é retirar a marca root da pessoa (`PUT /admin/users/:userId/root`, RN-AUT-15),
 * com a justificativa na trilha. A conta e os vínculos com clínicas continuam.
 */
export function useRemoveFromTeam(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (justification: string) =>
      api.put(`/admin/users/${id}/root`, { isRoot: false, justification }),
    onSuccess: () => {
      // O detalhe fica vencido no cache, sem recarregar: a tela volta para a lista.
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(id), refetchType: 'none' })
      return queryClient.invalidateQueries({ queryKey: ['team', 'list'] })
    },
  })
}
